/**
 * Image Compression Utility
 * 
 * Automatically compresses images only when necessary to meet file size requirements.
 * Preserves original quality when possible and only compresses when files are too large.
 */

/**
 * Compress an image file if it exceeds the specified size limit
 * @param {File} file - The image file to potentially compress
 * @param {Object} options - Compression options
 * @param {number} options.maxFileSize - Maximum file size in bytes (default: 8MB)
 * @param {number} options.maxDimension - Maximum width or height in pixels (default: 2048)
 * @param {number} options.qualityStep - How much to reduce quality each iteration (default: 0.1)
 * @param {number} options.minQuality - Minimum quality to maintain (default: 0.3)
 * @param {boolean} options.forceCompress - Force compression even if file is small enough (default: false)
 * @returns {Promise<File>} - The original file or compressed file
 */
export const compressImageIfNeeded = (file, options = {}) => {
  return new Promise((resolve, reject) => {
    // Configuration with defaults
    const {
      maxFileSize = 8 * 1024 * 1024, // 8MB default
      maxDimension = 2048,
      qualityStep = 0.1,
      minQuality = 0.3,
      forceCompress = false
    } = options;

    // Validate input
    if (!file || !(file instanceof File)) {
      reject(new Error('Invalid file provided'));
      return;
    }

    // Check if it's an image file
    if (!file.type.startsWith('image/')) {
      // Not an image, return as-is
      resolve(file);
      return;
    }

    // If file is already small enough and we're not forcing compression, check dimensions only
    if (!forceCompress && file.size <= maxFileSize && file.type === 'image/jpeg') {
      const img = new Image();
      img.onload = () => {
        if (img.width <= maxDimension && img.height <= maxDimension) {
          // File is perfect as-is
          resolve(file);
        } else {
          // Need to resize only
          processImageWithCanvas();
        }
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        processImageWithCanvas();
      };
      img.src = URL.createObjectURL(file);
      return;
    }

    // Need to process the image
    processImageWithCanvas();

    function processImageWithCanvas() {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let { width, height } = img;
          
          if (width > maxDimension || height > maxDimension) {
            const aspectRatio = width / height;
            if (width > height) {
              width = maxDimension;
              height = width / aspectRatio;
            } else {
              height = maxDimension;
              width = height * aspectRatio;
            }
          }

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw the image on canvas
          ctx.drawImage(img, 0, 0, width, height);

          // Function to get compressed blob
          const tryCompress = (quality) => {
            return new Promise((resolveCompress) => {
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    resolveCompress(blob);
                  } else {
                    resolveCompress(null);
                  }
                },
                'image/jpeg',
                quality
              );
            });
          };

          // Compress with different quality levels until size is acceptable
          const compress = async () => {
            let quality = 0.9;
            let compressedBlob = null;

            while (quality >= minQuality) {
              compressedBlob = await tryCompress(quality);
              
              if (compressedBlob && compressedBlob.size <= maxFileSize) {
                break;
              }
              
              quality -= qualityStep;
            }

            // If still too large even at minimum quality, try smaller dimensions
            if (!compressedBlob || compressedBlob.size > maxFileSize) {
              // Reduce dimensions by 20% and try again
              const newWidth = width * 0.8;
              const newHeight = height * 0.8;
              
              canvas.width = newWidth;
              canvas.height = newHeight;
              ctx.drawImage(img, 0, 0, newWidth, newHeight);
              
              compressedBlob = await tryCompress(minQuality);
            }

            if (!compressedBlob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            // Create a new File object from the blob
            const compressedFile = new File(
              [compressedBlob],
              file.name.replace(/\.[^/.]+$/, '.jpg'), // Change extension to .jpg
              {
                type: 'image/jpeg',
                lastModified: Date.now()
              }
            );

            resolve(compressedFile);
          };

          compress();

        } catch (error) {
          reject(error);
        } finally {
          URL.revokeObjectURL(img.src);
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image for processing'));
      };

      // Load the image
      img.src = URL.createObjectURL(file);
    }
  });
};

/**
 * Get compression info for display purposes
 * @param {File} originalFile - Original file
 * @param {File} compressedFile - Compressed file
 * @returns {Object} - Compression statistics
 */
export const getCompressionInfo = (originalFile, compressedFile) => {
  return {
    originalSize: originalFile.size,
    compressedSize: compressedFile.size,
    originalSizeMB: (originalFile.size / 1024 / 1024).toFixed(2),
    compressedSizeMB: (compressedFile.size / 1024 / 1024).toFixed(2),
    compressionRatio: ((1 - compressedFile.size / originalFile.size) * 100).toFixed(1),
    wasCompressed: compressedFile.size < originalFile.size
  };
};

/**
 * Validate if a file type is supported for compression
 * @param {File} file - File to check
 * @returns {boolean} - Whether the file type is supported
 */
export const isImageFile = (file) => {
  if (!file || !file.type) return false;
  return file.type.startsWith('image/');
};

/**
 * Get appropriate compression settings based on use case
 * @param {string} useCase - The use case ('offer', 'proof', 'profile', 'general')
 * @returns {Object} - Compression options
 */
export const getCompressionOptions = (useCase = 'general') => {
  const options = {
    general: {
      maxFileSize: 8 * 1024 * 1024, // 8MB
      maxDimension: 2048,
      qualityStep: 0.1,
      minQuality: 0.3
    },
    offer: {
      maxFileSize: 8 * 1024 * 1024, // 8MB (stay under 10MB limit)
      maxDimension: 2048,
      qualityStep: 0.1,
      minQuality: 0.4 // Slightly higher quality for proposals
    },
    proof: {
      maxFileSize: 8 * 1024 * 1024, // 8MB (stay under 10MB limit) 
      maxDimension: 2048,
      qualityStep: 0.1,
      minQuality: 0.5 // Higher quality for proofs
    },
    profile: {
      maxFileSize: 3 * 1024 * 1024, // 3MB (stay under 5MB limit)
      maxDimension: 1024, // Smaller for profile pics
      qualityStep: 0.15,
      minQuality: 0.4
    }
  };

  return options[useCase] || options.general;
};